import React, {ChangeEvent, FC, useCallback, useState} from 'react';
import './RecurringCosts.less';
import * as DateUtil from '../../../lib/dateutils';
import {CampaignRecurringCosts} from "../../../entities/campaign";
import useStateWithCallback from "../../../hooks/useStateWithCallback";

/**
 * Component to edit recurring costs for a campaign.
 *
 * Lists one or more items, with an associated cost.
 *
 * 08/15/2018 - 09/14/2018 .....  $250
 * 09/15/2018 - 10/14/2018 .....  $150
 * 10/15/2018 - 11/14/2018 .....  $200
 * -----------------------------------
 *                                $600
 *
 */
const RecurringCosts: FC<RecurringCostsProps> = props => {
    const [overrideIndex, setOverrideIndex] = useState<number>(-1);
    const [overrideCost, setOverrideCost] = useStateWithCallback<number>(null);

    /**
     * Update the cost for a period.
     *
     * Note that the displayed values are in reverse chronological order.
     */
    const handleCostBlur = useCallback((e: ChangeEvent<HTMLInputElement>, index: number) => {
        const costs = [...props.costs];
        costs[index].cost = Number.parseFloat(e.target.value);

        setOverrideIndex(-1);
        setOverrideCost(null, () => props.onChange(costs));
    }, [setOverrideCost, props]);

    const handleCostChange = useCallback((e: ChangeEvent<HTMLInputElement>, index: number) => {
        setOverrideCost(Number(e.target.value));
        setOverrideIndex(index);
    }, [setOverrideCost]);

    /**
     * Render a single line
     */
    const renderLine = useCallback((recurringCost: CampaignRecurringCosts, index: number) => {
        const startDate = DateUtil.formatDate(DateUtil.parseDate(recurringCost.startDate), false, false, false);
        const endDate = DateUtil.formatDate(DateUtil.parseDate(recurringCost.endDate), false, false, false);

        return (
            <div key={startDate + index} className='recurring-costs__line'>

                {/* Item Name */}
                <div className='recurring-costs__start-date'>{startDate} - {endDate}</div>

                <span className='recurring-costs__spacer'>$</span>

                {/* Per-Item Cost */}
                <input className='recurring-costs__cost'
                       type='number'
                       min='0'
                       step='.01'
                       value={index === overrideIndex ? overrideCost : recurringCost.cost}
                       onChange={e => handleCostChange(e, index)}
                       onBlur={e => handleCostBlur(e, index)}
                />
            </div>
        );
    }, [overrideIndex, overrideCost, handleCostChange, handleCostBlur]);

    return (
        <div className='recurring-costs'>
            <div className='recurring-costs__items'>
                {props.costs.map((recurringCost, index) => renderLine(recurringCost, index))}
            </div>
        </div>
    );

}

export type RecurringCostsProps = {
    // These are the fields stored in the database, but they may not be complete as costs may need to be
    // manually entered, which only happens if they have recently visited the campaign.
    costs: CampaignRecurringCosts[],

    // Used by ContentBlock
    isEditing?: boolean,
    isExpanded?: boolean,
    onChange?: (costs: CampaignRecurringCosts[]) => void,
};

export default RecurringCosts;
